import gql from 'graphql-tag';

import { Mutation, Query } from 'core/index';

import { PROFILE_THREAD_CONNECTION_FIELDS } from 'apps/profile/gql';
import { PROJECT_CONNECTION_FIELDS } from 'apps/project/gql';
import { STORY_CONNECTION_FIELDS, STORY_LIST_FIELDS } from 'apps/story/gql';
import { COLLECTION_CONNECTION_FIELDS } from 'apps/collection/gql';
import { CONTENT_FIELDS } from 'apps/content/gql';
import { USER_ERROR_FIELDS } from 'core/errors';

export const CHANNEL_FIELDS = gql`
  ${CONTENT_FIELDS}
  ${STORY_LIST_FIELDS}
  fragment channelFields on Channel {
    id
    avatar {
      id
      path
      url
    }
    name
    description
    content {
      ...contentFields
    }
    moderators {
      id
      username
      fullName
      avatar {
        id
        path
        url
      }
    }
    inSpace {
      id
      whichTypes
    }
    featuredIn
    ctas
    configuration
    story {
      id
      title
      body
      avatar {
        id
        path
        url
        completed
      }
    }
  }
`;

export const CHANNEL_CONNECTION_FIELDS = gql`
  fragment channelConnectionFields on ChannelConnection {
    pageInfo {
      startCursor
      endCursor
      hasNextPage
    }
    totalCount
    edges {
      cursor
      node {
        id
        name
        taggedCount
        hasModerators
      }
    }
  }
`;

export const CHANNEL_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation channel($channelInput: ChannelInput) {
    channelResult(channelInput: $channelInput) {
      result {
        ...channelFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelMutation extends Mutation {
  static conf = {
    GQL: CHANNEL_MUTATION,
    inputName: 'channelInput',
    resultPath: 'channel.channel',
    errorPath: 'channel.userErrors',
  };
}

const CHANNEL_QUERY = gql`
  ${CHANNEL_FIELDS}
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  ${PROJECT_CONNECTION_FIELDS}
  ${STORY_CONNECTION_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Channel(
    $slug: String
    $threadCursor: String
    $threadBatchSize: Int
    $threadSortBy: String
    $projectsCursor: String
    $projectsSortBy: String
    $projectsCount: Int
    $storiesCursor: String
    $storiesSortBy: String
    $storiesCount: Int
    $collectionsCount: Int
    $collectionsCursor: String
    $collectionsSortBy: String
  ) {
    channel(slug: $slug) {
      result {
        ...channelFields
        threads(
          first: $threadBatchSize
          after: $threadCursor
          sortBy: $threadSortBy
        ) {
          ...profileThreadConnectionFields
        }
        projects(
          tag: $slug
          first: $projectsCount
          after: $projectsCursor
          sortBy: $projectsSortBy
        ) {
          ...projectConnectionFields
        }
        stories(
          tag: $slug
          first: $storiesCount
          after: $storiesCursor
          sortBy: $storiesSortBy
        ) {
          ...storyConnectionFields
        }
        collections(
          tag: $slug
          first: $collectionsCount
          after: $collectionsCursor
          sortBy: $collectionsSortBy
        ) {
          ...collectionConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelQuery extends Query {
  static conf = {
    GQL: CHANNEL_QUERY,
    varNames: [
      'slug',
      'threadCursor',
      'threadSortBy',
      'threadBatchSize',
      'projectsCursor',
      'projectsSortBy',
      'projectsCount',
      'storiesCursor',
      'storiesCount',
      'storiesSortBy',
      'collectionsCursor',
      'collectionsSortBy',
      'collectionsCount',
    ],
    resultPath: 'channel.result',
    errorPath: 'channel.userErrors',
  };
}

const CHANNELS_QUERY = gql`
  ${CHANNEL_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query Channels($batchSize: Int, $cursor: String, $sortBy: String) {
    channels(first: $batchSize, after: $cursor, sortBy: $sortBy) {
      result {
        ...channelConnectionFields
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;
export class ChannelsQuery extends Query {
  static conf = {
    GQL: CHANNELS_QUERY,
    varNames: ['batchSize', 'cursor', 'sortBy'],
    resultPath: 'channels.result',
    errorPath: 'channels.userErrors',
  };
}

export const CHANNEL_PROJECTS = gql`
  ${PROJECT_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query ChannelProjects(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    channel(slug: $slug) {
      result {
        id
        projects(
          tag: $slug
          first: $batchSize
          after: $cursor
          sortBy: $sortBy
        ) {
          ...projectConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelProjectsQuery extends Query {
  static conf = {
    GQL: CHANNEL_PROJECTS,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'channel.result.projects',
    errorPath: 'channel.userErrors',
  };
}

const CHANNEL_STORIES = gql`
  ${STORY_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query ChannelStories(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    channel(slug: $slug) {
      result {
        id
        stories(
          tag: $slug
          first: $batchSize
          after: $cursor
          sortBy: $sortBy
        ) {
          ...storyConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelStoriesQuery extends Query {
  static conf = {
    GQL: CHANNEL_STORIES,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'channel.result.stories',
    errorPath: 'channel.userErrors',
  };
}

const CHANNEL_COLLECTIONS = gql`
  ${USER_ERROR_FIELDS}
  ${COLLECTION_CONNECTION_FIELDS}
  query ChannelCollections(
    $slug: String
    $cursor: String
    $batchSize: Int
    $sortBy: String
  ) {
    channel(slug: $slug) {
      result {
        id
        collections(
          tag: $slug
          first: $batchSize
          after: $cursor
          sortBy: $sortBy
        ) {
          ...collectionConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelCollectionsQuery extends Query {
  static conf = {
    GQL: CHANNEL_COLLECTIONS,
    varNames: ['slug', 'cursor', 'sortBy', 'batchSize'],
    resultPath: 'channel.result.collections',
    errorPath: 'channel.userErrors',
  };
}

const CHANNEL_THREADS_QUERY = gql`
  ${PROFILE_THREAD_CONNECTION_FIELDS}
  ${USER_ERROR_FIELDS}
  query ChannelThreads(
    $slug: String
    $batchSize: Int
    $cursor: String
    $sortBy: String
  ) {
    channel(slug: $slug) {
      result {
        id
        threads(
          first: $threadBatchSize
          after: $threadCursor
          sortBy: $threadSortBy
        ) {
          ...profileThreadConnectionFields
        }
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ChannelThreadsQuery extends Query {
  static conf = {
    GQL: CHANNEL_THREADS_QUERY,
    varNames: ['slug', 'batchSize', 'cursor', 'sortBy'],
    resultPath: 'channel.result.threads',
    errorPath: 'channel.userErrors',
  };
}

export const MODERATOR_MUTATION = gql`
  ${USER_ERROR_FIELDS}
  mutation Moderator($moderatorInput: RequestInput) {
    groupRequest(requestData: $moderatorInput) {
      groupRequest {
        id
      }
      userErrors {
        ...userErrorFields
      }
    }
  }
`;

export class ModeratorFormMutation extends Mutation {
  static conf = {
    GQL: MODERATOR_MUTATION,
    inputName: 'moderatorInput',
    resultPath: 'moderator.moderator',
    errorPath: 'moderator.userErrors',
  };
}
